// src/components/Dashboard.js
import React, { useEffect, useState } from "react";
import { getContract } from "../utils";
import { ethers } from "ethers";
import CreatePool from "./CreatePool";
import JoinPool from "./JoinPool";
import WithdrawRewards from "./WithdrawRewards";
import ApproveUSDT from "./ApproveUSDT";
import { USDT_ADDRESS, CONTRACT_ADDRESS } from "../utils";
import Modal from "./Modal";
import '../App.css';

export default function Dashboard({ wallet, refPool, usdtBalance, contractBalance }) {
    const [pools, setPools] = useState([]);
    const [participatingPools, setParticipatingPools] = useState([]);
    const [createdPoolsByLevel, setCreatedPoolsByLevel] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [pendingJoin, setPendingJoin] = useState(null);
    const [isApproving, setIsApproving] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const [activePoolId, setActivePoolId] = useState(null);
    const [unlockedLevel, setUnlockedLevel] = useState(0);
    const [activeTab, setActiveTab] = useState("running");
    const [myTab, setMyTab] = useState("running");
    const [shownParticipants, setShownParticipants] = useState({});
    const [copiedId, setCopiedId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "" });
    const [exitingPoolId, setExitingPoolId] = useState(null);
    const [exitConfirmModal, setExitConfirmModal] = useState({ show: false, poolId: null, });
    const [showPools, setShowPools] = useState(false);
    const [showCreatedPools, setShowCreatedPools] = useState(false);
    const [showMoreLinks, setShowMoreLinks] = useState({});
    const [shareModalLevel, setShareModalLevel] = useState(null);

    const LEVELS = {
        0: { name: "Entry", max: 3, investment: 16, totalReward: 43, background: "bg-blue-100", image: "/images/entry.svg" },
        1: { name: "Stacker", max: 4, investment: 32, totalReward: 115, background: "bg-green-100", image: "/images/stacker.svg" },
        2: { name: "Scaler", max: 5, investment: 64, totalReward: 288, background: "bg-yellow-100", image: "/images/scaler.png" },
        3: { name: "Flipper", max: 6, investment: 128, totalReward: 691, background: "bg-orange-100", image: "/images/flipper.svg" },
        4: { name: "Holder", max: 5, investment: 256, totalReward: 1152, background: "bg-purple-100", image: "/images/holder.png" },
        5: { name: "Broker", max: 4, investment: 512, totalReward: 1843, background: "bg-red-100", image: "/images/broker.png" },
        6: { name: "Tycoon", max: 3, investment: 1024, totalReward: 2765, background: "bg-pink-100", image: "/images/tycoon.svg" },
        7: { name: "Legend", max: 10, investment: 2048, totalReward: 18432, background: "bg-indigo-100", image: "/images/legend.png" }
    };

    const showAlert = (title, message) => {
        setAlertModal({ show: true, title, message });
    };

    const poolStatusMap = {
        0: "Available",
        1: "Full",
        2: "Cancelled"
    };

    const toggleParticipants = (id) => {
        setShownParticipants((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getClickbaitMessage = (level) => {
        switch (level.name) {
            case "Entry":
                return `🚀 Jump in with just ${level.investment} USDT and invite ${level.max} friends to earn approximately ${level.totalReward} USDT!`;
            case "Stacker":
                return `🏗️ Level Up: Invest ${level.investment} USDT, invite ${level.max} friends, and earn roughly ${level.totalReward} USDT!`;
            case "Scaler":
                return `📈 Grow Big! With a ${level.investment} USDT investment and ${level.max} friends, earn near ${level.totalReward} USDT!`;
            case "Flipper":
                return `💥 Flip Your Fortune! Only ${level.investment} USDT and ${level.max} allies needed for nearly ${level.totalReward} USDT!`;
            case "Holder":
                return `🔒 Secure Your Spot: Invest ${level.investment} USDT and recruit ${level.max} friends to earn around ${level.totalReward} USDT!`;
            case "Broker":
                return `💼 Broker Mode: With ${level.investment} USDT and ${level.max} friends, unlock nearly ${level.totalReward} USDT!`;
            case "Tycoon":
                return `👑 Dominate the scene: Invest ${level.investment} USDT, lead ${level.max} allies, and earn about ${level.totalReward} USDT!`;
            case "Legend":
                return `⭐ VIP Alert: Invest ${level.investment} USDT, rally ${level.max} elite allies, and unlock an exclusive reward of ${level.totalReward} USDT! Welcome to the elite league.`;
            default:
                return "";
        }
    };

    const fetchUnlockedLevel = async () => {
        try {
            const contract = getContract(wallet.signer);
            const level = await contract.getUnlockedLevel(wallet.address);
            setUnlockedLevel(Number(level));
        } catch (err) {
            console.error("Failed to fetch unlocked level:", err);
        }
    };

    // Update fetchAllPools to also include globalId (the key used in the contract)
    const fetchAllPools = async () => {
        try {
            const contract = getContract(wallet.signer);
            const allPoolsRaw = await contract.getAllPools();

            const poolList = allPoolsRaw.map((pool, index) => {
                const parsedParticipants = pool.participants.map(p => p.toLowerCase());
                return {
                    globalId: index + 1,
                    localId: pool.id !== undefined && pool.id !== null ? pool.id.toString() : (index + 1).toString(),
                    level: Number(pool.level),
                    center: pool.center,
                    participants: parsedParticipants,
                    status: Number(pool.status),
                    rewardClaimed: pool.rewardClaimed
                };
            });

            setPools(poolList);

            // For example, build a list of created pools by level (use localId for referral links)
            const createdByLevel = {};
            poolList.forEach((b) => {
                if (b.center.toLowerCase() === wallet.address.toLowerCase() && b.status === 0) {
                    const lvl = b.level;
                    if (!createdByLevel[lvl]) createdByLevel[lvl] = [];
                    createdByLevel[lvl].push({ level: lvl, localId: b.localId, globalId: b.globalId });
                }
            });
            setCreatedPoolsByLevel(createdByLevel);

            // (Also update any “participating” pools list as needed using the globalId.)
            const participating = poolList
                .filter((b) =>
                    b.participants.map((p) => p.toLowerCase()).includes(wallet.address.toLowerCase())
                )
                .map((b) => b.globalId.toString());
            setParticipatingPools(participating);
        } catch (error) {
            console.error("Error fetching pools:", error);
            /* showAlert("Error loading pools. Please try again."); */
        }
    };

    const myPools = pools.filter((b) => b.center.toLowerCase() === wallet.address.toLowerCase());

    const filteredMyPools = myPools.filter((b) => {
        if (myTab === "all") return true;
        if (myTab === "running") return b.status === 0;
        if (myTab === "ended") return b.status === 1;
        if (myTab === "cancelled") return b.status === 2;
        return true;
    });

    const filteredPools = pools.filter((b) => {
        if (activeTab === "all") return true;
        if (activeTab === "running") return b.status === 0;
        if (activeTab === "ended") return b.status === 1;
        if (activeTab === "cancelled") return b.status === 2;
        return true;
    });

    const exitPool = async (poolId) => {
        try {
            const contract = getContract(wallet.signer);

            // Find the pool info from local state
            const pool = pools.find((b) => b.globalId === poolId);
            if (!pool) throw new Error("Pool not found");

            const level = pool.level;
            const investment = LEVELS[level].investment;
            const refund = (investment * 0.1).toFixed(2); // 10% of the investment

            const tx = await contract.exitPool(poolId);
            await tx.wait();

            showAlert(
                "Pool cancelled successfully",
                `🚨 You received a refund of ${refund} USDT.\n\n💸 This represents 10% of your original investment at the ${LEVELS[level].name} level.`
            );

            fetchAllPools();
        } catch (error) {
            console.error("Exit failed:", error);
            showAlert(
                "Failed to exit pool",
                "❌ You may not be the creator or the pool might already be full."
            );
        }
    };

    // Example function to join a pool from the global dashboard
    // IMPORTANT: This function now receives the pool's globalId.
    const joinPool = async (globalPoolId, investmentAmount) => {
        try {
            setActivePoolId(globalPoolId);
            setIsJoining(true);

            const contract = getContract(wallet.signer);

            const usdtContract = new ethers.Contract(
                USDT_ADDRESS,
                ["function allowance(address owner, address spender) view returns (uint256)"],
                wallet.provider
            );

            const allowance = await usdtContract.allowance(wallet.address, CONTRACT_ADDRESS);
            const requiredAmount = ethers.parseUnits(investmentAmount.toString(), 18);

            if (allowance < requiredAmount) {
                setPendingJoin({ poolId: globalPoolId, requiredAmount });
                setShowModal(true);
                setIsJoining(false);
                return;
            }

            // Use the globalPoolId in the contract call
            const tx = await contract.joinPool(globalPoolId);
            await tx.wait();

            // Find the joined pool details using globalId instead of local id.
            const joinedPool = pools.find((b) => b.globalId.toString() === globalPoolId.toString());
            const poolName = joinedPool && LEVELS[joinedPool.level] ? LEVELS[joinedPool.level].name : "";
            const localId = joinedPool?.localId || globalPoolId;
            showAlert("Success", `✅ Successfully joined Pool ${poolName} ID #${localId}`);
        } catch (err) {
            console.error("Error joining pool:", err);

            let message = "Failed to join. Please try again.";

            if (err?.reason) {
                message = err.reason;
            } else if (err?.error?.message) {
                message = err.error.message;
            } else if (err?.data?.message) {
                message = err.data.message;
            } else if (err?.message) {
                message = err.message;
            }

            if (message.includes("execution reverted:")) {
                message = message.split("execution reverted:")[1].trim();
            } else if (message.includes("revert")) {
                message = message.split("revert")[1].trim();
            }

            if (message.toLowerCase().includes("user rejected")) {
                message = "Transaction cancelled by user.";
            }

            // Reset the loading state before or immediately after alert
            setIsJoining(false);
            setActivePoolId(null);

            showAlert("❌ Join Pool Failed", message);
            await fetchAllPools();
            await fetchUnlockedLevel();
        }

    };


    const handleApproveAndJoin = async () => {
        try {
            if (!pendingJoin) return;
            setIsApproving(true);

            const usdtContract = new ethers.Contract(
                USDT_ADDRESS,
                ["function approve(address spender, uint256 amount) public returns (bool)"],
                wallet.signer
            );

            const approveTx = await usdtContract.approve(CONTRACT_ADDRESS, pendingJoin.requiredAmount);
            await approveTx.wait();

            setShowModal(false);
            await joinPool(pendingJoin.poolId, ethers.formatUnits(pendingJoin.requiredAmount, 18));
        } catch (err) {
            console.error("Approval failed:", err);
            showAlert("Approval failed. Please try again.");
        } finally {
            setIsApproving(false);
        }
    };

    const renderShareableLinks = (levelId) => {
        const ids = createdPoolsByLevel[levelId] || [];
        if (!ids.length) {
            return <span className="share-placeholder">🔗 Your referral share links here.</span>;
        }

        const inline = ids.slice(0, 1);

        return (
            <div className="share-links-wrapper">
                <div className="share-label">🔗 Share Pool Links:<br /></div>
                <div className="share-buttons">
                    {inline.map(({ localId }) => (
                        <button
                        key={`${levelId}-${localId}`}
                        onClick={() => {
                          const url = `${window.location.origin}/?ref=${levelId}-${localId}`;
                          copyToClipboard(url, localId);
                        }}
                        className="shareable-link px-2 py-1 bg-pink-100 rounded hover:bg-pink-200"
                      >
                        {copiedId === localId
                          ? 'Copied!'
                          : `#${localId} (${LEVELS[levelId].name})`}
                      </button>
                      
                    ))}
                </div>

                {ids.length > 3 && (
                    <span
                        onClick={() => setShareModalLevel(levelId)}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                setShareModalLevel(levelId);
                            }
                        }}
                        className="more-text"
                        role="button"
                        tabIndex={0}
                    >
                        (+{ids.length - 1} more)
                    </span>
                )}
            </div>
        );
    };


    const copyToClipboard = (text, localId) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          setCopiedId(localId);
          setTimeout(() => setCopiedId(null), 1500);
        } catch (err) {
          console.error('Copy failed fallback:', err);
          showAlert('Copy failed, please copy manually:\n' + text);
        }
        document.body.removeChild(textarea);
      };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (wallet?.signer) {
            fetchUnlockedLevel();
            fetchAllPools();
            const interval = setInterval(fetchAllPools, 10000);
            return () => clearInterval(interval);
        }
    }, [wallet]);

    useEffect(() => {
        if (refPool) {
            console.log("Referral pool received:", refPool);
        }
    }, [refPool]);

    const renderColumnsLayout = () => {
        const levelOrder = Object.keys(LEVELS)
            .map(Number)
            .sort((a, b) => a - b);

        const columns = [
            levelOrder.slice(0, 3),
            levelOrder.slice(3, 6),
            levelOrder.slice(6),
        ];

        return (
            <div className="levels-overview columns-layout">
                {columns.map((col, colIdx) => (
                    <div
                        key={colIdx}
                        className={`column-group group-${colIdx}`}>
                        <div className="column">
                            {col.map((lvl) => {
                                const level = LEVELS[lvl];
                                return (
                                    <div
                                        key={lvl}
                                        className={`level-card level-${level.name.toLowerCase()}`}
                                    >
                                        <div className="card-header">
                                            <img
                                                src={level.image}
                                                alt={`${level.name} badge`}
                                                className="level-icon"
                                            />
                                            <h4>{level.name}</h4>
                                            <div className="info-tooltip-container">
                                                <span className="info-icon">i</span>
                                                <div className="tooltip-content">
                                                    {getClickbaitMessage(level)}
                                                </div>
                                            </div>
                                        </div>
                                        <p>Investment: {level.investment} USDT</p>
                                        <p>Friends: {level.max}</p>
                                        <p>Total Reward: {level.totalReward} USDT</p>

                                        {lvl === 0 || lvl <= unlockedLevel ? (
                                            <CreatePool
                                                wallet={wallet}
                                                USDTAddress={USDT_ADDRESS}
                                                level={lvl}
                                                investment={level.investment}
                                                onPoolCreated={() => {
                                                    fetchUnlockedLevel();
                                                    fetchAllPools();
                                                }}
                                            />
                                        ) : (
                                            <p className="text-xs text-gray-500 italic text-center">
                                                🔒 Locked — Unlock {level.name} by completing{' '}
                                                {LEVELS[lvl - 1].name}
                                            </p>
                                        )}
                                        {renderShareableLinks(lvl)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    const poolButtons = Object.entries(createdPoolsByLevel)
        .flatMap(([levelId, pools]) =>
            pools.map(({ localId, globalId }) => ({
                levelId: Number(levelId),
                localId,
                globalId,
            }))
        )
        .map(({ levelId, localId }) => (
            <button
  key={localId}
  onClick={() => {
    const url = `${window.location.origin}/?ref=${levelId}-${localId}`;
    copyToClipboard(url, localId);
  }}
  className="shareable-link px-2 py-1 bg-pink-100 rounded hover:bg-pink-200"
>
  {copiedId === localId ? 'Copied!' : `#${localId} (${LEVELS[levelId].name})`}
</button>
        ));


    return (
        <div className="mt-8">
            {shareModalLevel !== null && (
                <Modal isOpen onClose={() => setShareModalLevel(null)}>
                    <div className="p-4">
                        <h3 className="font-bold text-lg mb-2">
                            🔗 All share links for {LEVELS[shareModalLevel].name}
                        </h3>
                        <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                            {createdPoolsByLevel[shareModalLevel].map(({ localId }) => (
                                <button
                                key={localId}
                                onClick={() => {
                                  const url = `${window.location.origin}/?ref=${shareModalLevel}-${localId}`;
                                  copyToClipboard(url, localId);
                                  setTimeout(() => setShareModalLevel(null), 1000);
                                }}
                                className="shareable-link px-2 py-1 bg-pink-100 rounded hover:bg-pink-200"
                              >
                                {copiedId === localId ? 'Copied!' : `#${localId} (${LEVELS[shareModalLevel].name})`}
                              </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShareModalLevel(null)}
                            className="modal-close-btn"
                        >
                            Close
                        </button>
                    </div>
                </Modal>
            )}

            <div className="section-frame">
                <div className="dashboard-header">
                    {/* Global Pool banner */}
                    <div className="growth-pool-banner">
                        🏦 <span className="glow-text">Global Pool</span> 🏦 &nbsp;
                        💰 {contractBalance} USDT
                    </div>

                    {/* Wallet info */}
                    <div className="dashboard-wallet-info">
                        Your Wallet Address<p className="wallet-address">{wallet.address}</p>
                        <p className="usdt-title">Your USDT Balance</p><p className="usdt-balance">{usdtBalance}</p>
                    </div>
                </div>
                <ApproveUSDT
                    wallet={wallet}
                    levelInvestments={[16, 32, 64, 128, 256, 512, 1024, 2048]}
                />
                {/* {refPool && (
                    <div className="referral-info text-lg font-bold text-blue-600">
                        🎯 Referral Pool: {LEVELS[refPool.level]?.name}, Pool #{refPool.localId}
                    </div>
                )} */}

                <JoinPool wallet={wallet} refPool={refPool} />

                <p className="unlocked-level">
                    🎯 Unlocked Level:{' '}
                    <span
                        className={`unlocked-level-badge badge-${LEVELS[
                            unlockedLevel
                        ]?.name.toLowerCase()}`}
                    >
                        {LEVELS[unlockedLevel]?.name || unlockedLevel}
                    </span>
                </p>

                {participatingPools.length > 0 && (
                    <div className="participating-container">
                        <strong>You are participating in pool IDs:</strong>
                        <div className="participating-list">
                            {(showPools
                                ? participatingPools
                                : participatingPools.slice(0, 3)
                            ).map((id) => (
                                <span key={id} className="participating-item">
                                    {id}
                                </span>
                            ))}

                            {participatingPools.length > 3 && (
                                <button
                                    onClick={() => setShowPools((prev) => !prev)}
                                    className="participating-toggle"
                                >
                                    ({showPools ? 'Show Less' : 'Show More'})
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {poolButtons.length > 0 && (
                    <div className="created-pools-frame">
                        <strong>Your Active Pools:</strong>
                        <div className="font-semibold text-blue-600 flex flex-wrap gap-2 mt-1">
                            {showCreatedPools ? poolButtons : poolButtons.slice(0, 3)}
                        </div>
                        {poolButtons.length > 3 && (
                            <button
                                onClick={() => setShowCreatedPools((prev) => !prev)}
                                className="created-toggle"
                            >
                                ({showCreatedPools ? 'Show Less' : 'Show More'})
                            </button>
                        )}
                    </div>
                )}
            </div>

            <WithdrawRewards wallet={wallet} fetchAllPools={fetchAllPools} fetchUnlockedLevel={fetchUnlockedLevel} />

            <h2 className="overview-header">Levels Overview</h2>
            {renderColumnsLayout()}


            <div className="my-tabs-frame">
                <h2 className="my-tabs-title">My Pools</h2>
                <div className="my-tabs-container">
                    <button
                        className={`my-tab ${myTab === "running" ? "active" : ""}`}
                        onClick={() => setMyTab("running")}
                    >
                        Running
                    </button>
                    <button
                        className={`my-tab ${myTab === "ended" ? "active" : ""}`}
                        onClick={() => setMyTab("ended")}
                    >
                        Ended
                    </button>
                    <button
                        className={`my-tab ${myTab === "cancelled" ? "active" : ""}`}
                        onClick={() => setMyTab("cancelled")}
                    >
                        Cancelled
                    </button>
                    <button
                        className={`my-tab ${myTab === "all" ? "active" : ""}`}
                        onClick={() => setMyTab("all")}
                    >
                        All Pools
                    </button>
                </div>
                <div className="pool-grid relative">
                    {filteredMyPools.length > 0 ? (
                        filteredMyPools.map((b) => (
                            <div key={b.globalId} className={`pool-item card-${LEVELS[b.level].name.toLowerCase()}`}>
                                <h3 className="font-semibold text-lg">Pool # {b.localId}<br />{LEVELS[b.level].name}</h3>
                                <p>
                                    <strong>Investment:</strong> {b.participants.length} / {LEVELS[b.level].max}
                                </p>
                                <p>
                                    <strong>Status:</strong> {poolStatusMap[b.status]}
                                </p>
                                <p>
                                    <strong>Reward:</strong> {LEVELS[b.level].totalReward} USDT
                                </p>
                                {b.status !== 2 && (
                                    <button
                                        onClick={() => toggleParticipants(b.globalId)}
                                        className={`toggle-button level-${b.level} ${shownParticipants[b.globalId] ? "hide" : "show"}`}
                                    >
                                        {shownParticipants[b.globalId] ? "Collapse" : "View Allies"}
                                    </button>
                                )}
                                {shownParticipants[b.globalId] && (
                                    <div className="participant-chain fade-in">
                                        {b.participants.length > 0 ? (
                                            b.participants.map((p, i) => (
                                                <div key={i} className="participant-pool">
                                                    <span>...{p.slice(-4)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="italic text-gray-500 text-sm">No Friends Yet...</div>
                                        )}
                                    </div>
                                )}

                                {b.status === 0 &&
                                    b.center.toLowerCase() === wallet.address.toLowerCase() &&
                                    b.participants.length === 0 && (
                                        <button
                                            onClick={() => setExitConfirmModal({ show: true, poolId: b.globalId })}
                                            className="emergency-exit-button mt-2 w-full py-2 px-4 rounded-lg text-white font-semibold transition duration-300"
                                            disabled={exitingPoolId === b.globalId}
                                        >
                                            {exitingPoolId === b.globalId ? "🚨 Exiting..." : "🚨 Emergency Exit"}
                                        </button>
                                    )}
                            </div>
                        ))
                    ) : (
                        <div className="no-pools-message text-gray-500 italic text-center py-8">
                            {myTab === "ended" && "🔔 No Pools Ended yet."}
                            {myTab === "cancelled" && "🚫 No Pools Cancelled."}
                            {myTab === "running" && "🏃‍♂️ No Pools Running at the moment."}
                            {myTab === "all" && "📭 You have no Pools."}
                        </div>
                    )}
                </div>
            </div>


            <div className="tabs-frame">
                <h2 className="dashboard-title">Global Pools Dashboard 🌐</h2>
                <div className="tabs-container">
                    <button
                        className={`tab ${activeTab === "running" ? "active" : ""}`}
                        onClick={() => setActiveTab("running")}
                    >
                        Running
                    </button>
                    <button
                        className={`tab ${activeTab === "ended" ? "active" : ""}`}
                        onClick={() => setActiveTab("ended")}
                    >
                        Ended
                    </button>
                    <button
                        className={`tab ${activeTab === "cancelled" ? "active" : ""}`}
                        onClick={() => setActiveTab("cancelled")}
                    >
                        Cancelled
                    </button>
                    <button
                        className={`tab ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                    >
                        All Pools
                    </button>
                </div>
                <div className="pool-grid relative">
                    {filteredPools.map((b) => (
                        <div key={b.globalId} className={`pool-item card-${LEVELS[b.level].name.toLowerCase()}`}>
                            <div className="flex-center-column mb-4">
                                <h3 className="font-semibold text-lg text-center">
                                    Pool # {b.localId.toString()}<br />{LEVELS[b.level].name}
                                </h3>
                                <span className={`text-sm font-bold mt-1 ${b.status === 1 ? "text-red-500" : b.status === 2 ? "text-gray-500" : "text-green-600"}`}>
                                    {poolStatusMap[b.status]}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                <strong>Creator:</strong> ...{b.center.slice(-4)}
                            </p>
                            <p>
                                <strong>Investment:</strong> {LEVELS[b.level].investment} USDT
                            </p>
                            <p>
                                <strong>Friends:</strong> {b.participants.length} / {LEVELS[b.level].max}
                            </p>
                            <p>
                                <strong>Reward:</strong> {LEVELS[b.level].totalReward} USDT
                            </p>
                            {b.status !== 2 && (
                                <button
                                    onClick={() => toggleParticipants(b.globalId)}
                                    className={`toggle-button level-${b.level} ${shownParticipants[b.globalId] ? "hide" : "show"}`}
                                >
                                    {shownParticipants[b.globalId] ? "Collapse" : "View Allies"}
                                </button>
                            )}
                            {shownParticipants[b.globalId] && (
                                <div className="participant-chain fade-in">
                                    {b.participants.length > 0 ? (
                                        b.participants.map((p, i) => (
                                            <div key={i} className="participant-pool">
                                                <span>...{p.slice(-4)}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="italic text-gray-500 text-sm">No Friends Yet...</div>
                                    )}
                                </div>
                            )}
                            {b.status === 0 &&
                                b.center.toLowerCase() === wallet.address.toLowerCase() &&
                                b.participants.length === 0 && (
                                    <button
                                        onClick={() => setExitConfirmModal({ show: true, poolId: b.globalId })}
                                        className="emergency-exit-button mt-2 w-full py-2 px-4 rounded-lg text-white font-semibold transition duration-300"
                                        disabled={exitingPoolId === b.globalId}
                                    >
                                        {exitingPoolId === b.globalId ? "🚨 Exiting..." : "🚨 Emergency Exit"}
                                    </button>
                                )}
                            {b.status === 0 &&
                                !b.participants.some(addr => addr.toLowerCase() === wallet.address.toLowerCase()) &&
                                b.center.toLowerCase() !== wallet.address.toLowerCase() && (
                                    <>
                                        {b.level <= unlockedLevel ? (
                                            <button
                                                disabled={isJoining && activePoolId === b.globalId || isApproving}
                                                className={`mt-4 w-full btn-primary ${isJoining && activePoolId === b.globalId ? "btn-disabled" : ""}`}
                                                onClick={() => joinPool(b.globalId, LEVELS[b.level].investment)}
                                            >
                                                {isJoining && activePoolId === b.globalId ? "Joining..." : "Join Pool"}
                                            </button>
                                        ) : (
                                            <p className="text-xs text-gray-500 italic mt-3 text-center">
                                                🔒 Locked — Unlock {LEVELS[b.level].name} by completing {LEVELS[b.level - 1].name}
                                            </p>
                                        )}
                                    </>
                                )}

                        </div>
                    ))}
                </div>
            </div>
            {exitConfirmModal.show && (
                <Modal
                    isOpen={exitConfirmModal.show}
                    onClose={() => setExitConfirmModal({ show: false, poolId: null })}
                >
                    <div className="p-4 text-center">
                        <h3 className="text-lg font-bold mb-2">⚠️ Confirm Emergency Exit</h3>
                        <p className="text-sm mb-4 text-yellow-700">
                            Exiting this pool will refund only <strong>10%</strong> of your original investment. This action is irreversible.
                        </p>
                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                onClick={() => setExitConfirmModal({ show: false, poolId: null })}
                                className="btn-cancel px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    setExitingPoolId(exitConfirmModal.poolId);
                                    setExitConfirmModal({ show: false, poolId: null });
                                    await exitPool(exitConfirmModal.poolId);
                                    setExitingPoolId(null);
                                }}
                                className="btn-primary px-4 py-2"
                            >
                                Confirm Exit
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {showModal && (
                <Modal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setIsJoining(false);
                        setActivePoolId(null);
                    }}
                >
                    <div className="p-4 text-center">
                        <h3 className="text-md font-semibold mb-3">
                            USDT Approval Required
                        </h3>
                        <p className="text-sm mb-4">
                            Approve USDT to join this pool.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-4">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setIsJoining(false);
                                    setActivePoolId(null);
                                }}
                                className="btn-cancel px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApproveAndJoin}
                                disabled={isApproving}
                                className={`btn-primary px-4 py-2 ${isApproving ? "btn-disabled" : ""}`}
                            >
                                {isApproving ? "Approving..." : "Approve & Join"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {alertModal.show && (
                <Modal
                    isOpen={alertModal.show}
                    onClose={() => setAlertModal({ show: false, title: "", message: "" })}
                >
                    <div className="p-4">
                        <h3 className="font-bold text-xl mb-2">{alertModal.title}</h3>
                        <p className="mb-4 whitespace-pre-wrap">{alertModal.message}</p>
                        <button className="btn-primary px-4 py-2" onClick={() => setAlertModal({ show: false, title: "", message: "" })}>
                            Close
                        </button>
                    </div>
                </Modal>

            )}
        </div>
    );
}
